/**
 * Analytics.jsx
 * How published posts actually did.
 *
 * Figures come from a nightly pull, so the screen states when they were last
 * collected rather than implying they are live. A post published an hour ago
 * shows "not measured yet" instead of zeros, because zero and unknown are very
 * different things.
 */

import { useCallback, useEffect, useState } from 'react';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { api } from '../services/api.js';
import MediaPreview from '../components/MediaPreview.jsx';
import { Chart, Refresh, Warning, Link, Check, Recycle } from '../components/Icons.jsx';
import { formatLondon } from '../utils/time.js';
import BoostDialog from '../components/BoostDialog.jsx';
import BestTimes from '../components/BestTimes.jsx';
import RecycleDialog from '../components/RecycleDialog.jsx';

const WINDOWS = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
];

const n = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());
const platformName = (post) => (post.platform === 'facebook' ? 'Facebook' : 'Instagram');

export default function Analytics() {
  const { brands } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  // The post a boost is being set up for, if any.
  const [boosting, setBoosting] = useState(null);
  // ...and the one being added to the evergreen rotation.
  const [recycling, setRecycling] = useState(null);
  // Rotations that already exist, so the dialog offers them rather than
  // inviting a new one to be typed every time.
  const [categories, setCategories] = useState([]);
  // The post being taken down from Facebook, while Facebook answers.
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await api.analytics.get({ brandId: brandId || null, days }));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [brandId, days]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Opens the recycle dialog, having first asked which rotations this brand
   * already has — typing "testimonials" freshly each time is how a rotation
   * quietly becomes three rotations.
   */
  async function openRecycle(post) {
    setNote(null);
    setError(null);
    try {
      const existing = await api.evergreen.get(post.brand_id);
      setCategories(existing.categories ?? []);
    } catch {
      setCategories([]);
    }
    setRecycling(post);
  }

  /**
   * Deletes one post from Facebook or Instagram. Irreversible — its likes and
   * comments go with it — so it is always confirmed first, and the notice
   * says plainly whether the platform actually took it down.
   */
  async function removeFromPlatform(post) {
    const where = platformName(post);
    const other = post.platform === 'facebook' ? 'Instagram' : 'Facebook';
    const preview = (post.caption ?? '').slice(0, 60);
    const ok = window.confirm(
      `Delete this post from ${post.brand_name}'s ${where}?\n\n"${preview}…"\n\n` +
        'Its likes and comments are deleted with it, and this cannot be undone. ' +
        `The ${other} version, if there is one, stays up.`,
    );
    if (!ok) return;

    setRemoving(post.target_id);
    setNote(null);
    setError(null);
    try {
      const res = await api.posts.removeFromPlatform(post.target_id);
      setNote(res.message ?? `Removed from ${where}.`);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setRemoving(null);
    }
  }

  async function refresh() {
    setBusy(true);
    setNote(null);
    setError(null);
    try {
      const res = await api.analytics.refresh();
      setNote(
        res.permissionProblem ??
          `Updated ${res.stored} of ${res.checked} posts` +
            (res.skipped ? `, ${res.skipped} Meta could not show` : '') +
            (res.failed - (res.skipped ?? 0) > 0
              ? `, ${res.failed - res.skipped} failed`
              : '') +
            '.',
      );
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (error && !data) {
    return (
      <div className="notice notice--error">
        <Warning />
        <span>{error.message}</span>
      </div>
    );
  }

  if (!data) return <p className="mono">Loading…</p>;

  const totals = Object.fromEntries(data.totals.map((t) => [t.platform, t]));
  const combined = data.totals.reduce(
    (acc, t) => ({
      posts: acc.posts + t.posts,
      reach: acc.reach + t.reach,
      impressions: acc.impressions + t.impressions,
      engagement: acc.engagement + t.likes + t.comments + t.shares + t.saves,
    }),
    { posts: 0, reach: 0, impressions: 0, engagement: 0 },
  );

  const best = [...data.posts]
    .filter((p) => p.fetched_at)
    .sort((a, b) => (b.reach ?? 0) - (a.reach ?? 0))
    .slice(0, 3);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Analytics</div>
          <h1>How your posts did</h1>
          <p>
            {data.lastFetch
              ? `Collected ${formatLondon(data.lastFetch)}. Meta updates figures for days after a post goes out.`
              : 'Nothing collected yet. Figures arrive with the nightly pull, or press Update now.'}
          </p>
        </div>
        <div className="page-head__actions">
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            <option value="">All brands</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {WINDOWS.map((w) => (
              <option key={w.days} value={w.days}>
                {w.label}
              </option>
            ))}
          </select>
          <button className="btn btn--primary" onClick={refresh} disabled={busy}>
            <Refresh /> {busy ? 'Updating…' : 'Update now'}
          </button>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {note && (
        <div className="notice notice--info">
          <Check />
          <span>{note}</span>
        </div>
      )}

      {/* ---------- Summary ---------- */}
      <div className="grid">
        <section className="card">
          <div className="card__head">
            <span className="tile">
              <Chart size={21} />
            </span>
            <div className="card__head-text">
              <h2>Both platforms</h2>
              <p>{combined.posts} posts in this period</p>
            </div>
          </div>

          <div className="stat-row">
            <Stat label="Reach" value={combined.reach} hint="People who saw it" />
            <Stat label="Impressions" value={combined.impressions} hint="Times shown" />
            <Stat label="Engagement" value={combined.engagement} hint="Likes, comments, shares, saves" />
          </div>
        </section>

        <section className="card">
          <div className="card__head">
            <div className="card__head-text">
              <h2>By platform</h2>
              <p>Same window, split.</p>
            </div>
          </div>

          <div className="rows">
            {['facebook', 'instagram'].map((platform) => {
              const t = totals[platform];
              return (
                <div className="row" key={platform}>
                  <div>
                    <div className="row__label">
                      {platform === 'facebook' ? 'Facebook' : 'Instagram'}
                    </div>
                    <div className="row__sub">
                      {t ? `${t.posts} post${t.posts === 1 ? '' : 's'}` : 'Nothing published'}
                    </div>
                  </div>
                  <div className="row__end">
                    {t ? (
                      <>
                        <span className="badge">{n(t.reach)} reach</span>
                        <span className="badge">
                          {n(t.likes + t.comments + t.shares + t.saves)} engagement
                        </span>
                      </>
                    ) : (
                      '—'
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* ---------- When to post ---------- */}
      <BestTimes brandId={brandId} />

      {/* ---------- Best performers ---------- */}
      {best.length > 0 && (
        <section className="card">
          <div className="card__head">
            <div className="card__head-text">
              <h2>Best performing</h2>
              <p>By reach, in this period. Worth making more of.</p>
            </div>
          </div>

          <div className="rows">
            {best.map((p) => (
              <PostRow
                key={p.target_id}
                post={p}
                onBoost={setBoosting}
                onRecycle={openRecycle}
                onRemove={removeFromPlatform}
                removing={removing === p.target_id}
              />
            ))}
          </div>
        </section>
      )}

      {/* ---------- Everything ---------- */}
      <section className="card">
        <div className="card__head">
          <div className="card__head-text">
            <h2>Every published post</h2>
            <p>
              {data.measured} of {data.posts.length} measured so far.
            </p>
          </div>
        </div>

        {data.posts.length === 0 ? (
          <div className="empty">
            <div className="empty__art">
              <Chart size={30} />
            </div>
            <h3>Nothing published in this period</h3>
            <p>Publish something and its figures appear here after the nightly pull.</p>
          </div>
        ) : (
          <div className="rows">
            {data.posts.map((p) => (
              <PostRow
                key={p.target_id}
                post={p}
                onBoost={setBoosting}
                onRecycle={openRecycle}
                onRemove={removeFromPlatform}
                removing={removing === p.target_id}
              />
            ))}
          </div>
        )}
      </section>

      {recycling && (
        <RecycleDialog
          post={recycling}
          categories={categories}
          onClose={() => setRecycling(null)}
          onAdded={(name) => {
            setRecycling(null);
            setNote(
              `Added to the ${name} rotation. Give that rotation a posting time on the Evergreen ` +
                'screen and it starts coming round again.',
            );
          }}
        />
      )}

      {boosting && (
        <BoostDialog
          brand={{ id: boosting.brand_id, name: boosting.brand_name }}
          post={boosting}
          onClose={() => setBoosting(null)}
          onCreated={(result) => {
            setBoosting(null);
            setNote(
              `Boost created and paused — up to £${((result.dailyBudgetPence * result.durationDays) / 100).toFixed(2)}. ` +
                'Start it on the Ads screen when you are ready.',
            );
          }}
        />
      )}
    </>
  );
}

function PostRow({ post, onBoost, onRecycle, onRemove, removing }) {
  const measured = !!post.fetched_at;
  const url =
    post.platform === 'facebook'
      ? `https://www.facebook.com/${post.remote_id}`
      : `https://www.instagram.com/p/${post.remote_id}`;

  return (
    <div className="row">
      {post.media_id ? (
        <MediaPreview media={{ id: post.media_id, kind: 'image' }} className="row__thumb" />
      ) : (
        <span className="tile tile--sm">
          <Chart size={17} />
        </span>
      )}

      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="row__label">
          {post.brand_name} · {post.platform === 'facebook' ? 'Facebook' : 'Instagram'}
        </div>
        <div className="row__sub">{(post.caption ?? '').slice(0, 80)}…</div>
        <div className="row__sub">{formatLondon(post.published_at)}</div>
      </div>

      <div className="row__end" style={{ gap: 16 }}>
        {measured ? (
          <>
            <Metric label="reach" value={post.reach} />
            <Metric label="likes" value={post.likes} />
            <Metric label="comments" value={post.comments} />
            {post.saves !== null && <Metric label="saves" value={post.saves} />}
          </>
        ) : (
          <span className="badge">Not measured yet</span>
        )}
        {post.platform === 'facebook' && post.remote_id && (
          <button
            className="btn btn--sm"
            onClick={() => onBoost(post)}
            title="Put money behind this post"
          >
            Boost
          </button>
        )}
        {/* Recycling belongs here rather than on Drafts: the moment you want
            a post again is the moment you can see it did well. */}
        <button
          className="btn btn--sm"
          onClick={() => onRecycle(post)}
          title="Bring this post round again on a schedule"
        >
          <Recycle size={15} />
        </button>
        <a className="btn btn--sm" href={url} target="_blank" rel="noreferrer" title="Open the post">
          <Link size={15} />
        </a>
        {post.remote_id && (
          <button
            className="btn btn--sm btn--danger"
            onClick={() => onRemove(post)}
            disabled={removing}
            title={`Delete this post from ${platformName(post)}`}
          >
            {removing ? 'Removing…' : `Remove from ${platformName(post)}`}
          </button>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="stat">
      <div className="stat__value">{n(value)}</div>
      <div className="stat__label">{label}</div>
      <div className="stat__hint">{hint}</div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="metric">
      <div className="metric__value">{n(value)}</div>
      <div className="metric__label">{label}</div>
    </div>
  );
}
